import React from 'react';
import { Lien } from 'pages/Search/models';
import {
  Typography,
  List,
  Row,
  Col,
  Button,
  Divider,
  Spin,
  Alert,
  Dropdown,
  Menu,
  Icon,
} from 'antd';
import {
  AttributeValuePair,
  TableSizeNames,
  IconTypeNames,
  Sizes,
  PrintButton,
} from 'common';
import { formatDate, getAccessToken } from 'common/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store';
import { exportDetailsAsync } from 'pages/Search';
import ButtonGroup from 'antd/lib/button/button-group';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

const { Title, Text } = Typography;

const LienDetails: React.FC<{ searchId: number; lien: Lien }> = (props: {
  searchId: number;
  lien: Lien;
}) => {
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const { loading }: any = useSelector((state: AppState) => ({
    loading: state.searchExport.loading,
  }));

  const {
    documentTypeTitle,
    controlNumber,
    lienType,
    court,
    county,
    clerk,
    clerkSequence,
    effectiveDate,
    debtor,
    creditor,
    docketDate,
    block,
    lot,
    date,
    attorney,
    docType,
    satisfaction,
    controlDate,
    amount,
    indexNumber,
    accidentDate,
    remarks,
    totalDebtors,
    documentNumber,
    operatorId,
    sheriffsExecutions,
    against,
  } = props.lien;

  let totalDebtorsLabel = 'ERROR';
  let indexNumberLabel = 'ERROR';
  let dispositionLabel = 'ERROR';

  let attorneyHeader = 'ERROR';
  let debtorHeader = 'ERROR';
  let creditorHeader = 'ERROR';
  let dispositionHeader = 'ERROR';
  let againsHeader = 'ERROR';

  switch (lienType) {
    case '01':
      indexNumberLabel = 'Index # ';
      totalDebtorsLabel = 'Total Debtors ';
      dispositionLabel = 'Disposition ';

      attorneyHeader = 'Creditor Attorney';
      againsHeader = 'Against Whom Claimed';
      creditorHeader = 'Creditor';
      debtorHeader = 'Debtor / Corporation';
      dispositionHeader = 'Satisfaction Data';
      break;
    case '02':
      indexNumberLabel = 'Lien # ';
      totalDebtorsLabel = 'Total Debtors ';
      dispositionLabel = 'Type ';

      attorneyHeader = 'Lienor Attorney';
      againsHeader = 'Alleged Liable';
      creditorHeader = 'Claimant';
      debtorHeader = 'Recipient / Corporation';
      dispositionHeader = 'Satisfaction Data';
      break;
    case '03':
      indexNumberLabel = 'Index # ';
      totalDebtorsLabel = 'Total Blocks & Lots ';
      dispositionLabel = 'Disposition ';

      attorneyHeader = 'Lienor Attorney';
      againsHeader = 'Against Whom Claimed';
      creditorHeader = 'Lienor';
      debtorHeader = 'Owner / Corporation';
      dispositionHeader = 'Disposition Data';
      break;
    case '04':
      indexNumberLabel = 'Loan # ';
      totalDebtorsLabel = 'Total Blocks & Lots ';
      dispositionLabel = 'Disposition ';

      attorneyHeader = 'Lienor Attorney';
      againsHeader = 'Against Whom Claimed';
      creditorHeader = 'Lender';
      debtorHeader = 'Borrower / Corporation';
      dispositionHeader = 'Disposition Data';
      break;
    case '05':
      indexNumberLabel = 'Index # ';
      totalDebtorsLabel = 'Total Blocks & Lots ';
      dispositionLabel = 'Disposition ';

      attorneyHeader = 'Lienor Attorney';
      creditorHeader = '1st Named Plaintiff / Corporation';
      debtorHeader = '1st Named Defendant / Corporation';
      dispositionHeader = 'Disposition Data';
      break;
    case '06':
      indexNumberLabel = 'Index # ';
      totalDebtorsLabel = 'Total Blocks & Lots ';
      dispositionLabel = 'Disposition ';

      attorneyHeader = 'Lienor Attorney';
      creditorHeader = 'Claimant';
      debtorHeader = 'Owner / Corporation';
      dispositionHeader = 'Cancellation Data';
      break;
    case '07':
      indexNumberLabel = 'Index # ';
      totalDebtorsLabel = 'Total Debtors ';
      dispositionLabel = 'Type ';

      attorneyHeader = 'Lienor Attorney';
      debtorHeader = 'Debtor / Corporation';
      dispositionHeader = 'Satisfaction Data';
      break;
    default:
      break;
  }

  const handleDetailsExport = React.useCallback(
    (format: string, action: 'print' | 'save') => {
      if (account && inProgress === InteractionStatus.None) {
        getAccessToken(instance, account, inProgress).then((response) => {
          if (response?.accessToken)
            dispatch(
              exportDetailsAsync(
                props.lien,
                props.searchId,
                'lien',
                format,
                action,
                response.accessToken,
              ),
            );
        });
      }
    },
    [dispatch, instance, account, inProgress, props.lien, props.searchId],
  );

  return (
    <Spin spinning={loading} size={Sizes.LARGE} tip={'Printing...'}>
      <Row type="flex" align="middle" justify="space-between">
        <Typography>
          <Title level={4}>
            {`${documentTypeTitle} - Control Number ${controlNumber}`}
          </Title>

          {(lienType === '03' || lienType === '04') && (
            <Title level={4}>{`For Block: ${parseInt(block)} Lot: ${parseInt(
              lot,
            )} in ${county} COUNTY`}</Title>
          )}

          {(lienType === '01' || lienType === '02') && (
            <Title level={4}>{`For ${debtor.name} in ${county} COUNTY`}</Title>
          )}
        </Typography>
        <h1> LienDetails</h1>
        <ButtonGroup>
          <PrintButton
            size={TableSizeNames.SMALL}
            icon={IconTypeNames.PRINTER}
            onClick={() => handleDetailsExport('html', 'print')}
          />
          <Dropdown
            overlay={
              <Menu onClick={({ key }) => handleDetailsExport(key, 'save')}>
                <Menu.Item key="doc">
                  <Icon type="file-word" />
                  MS Word
                </Menu.Item>
                <Menu.Item key="pdf">
                  <Icon type="file-pdf" />
                  PDF
                </Menu.Item>
                <Menu.Item key="html">
                  <Icon type="html5" />
                  HTML
                </Menu.Item>
                <Menu.Item key="txt">
                  <Icon type="file-text" />
                  Text
                </Menu.Item>
              </Menu>
            }
          >
            <Button
              type="primary"
              size={TableSizeNames.SMALL}
              shape="round"
              icon="download"
            >
              Export&nbsp;
              <Icon type="down" />
            </Button>
          </Dropdown>
        </ButtonGroup>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <List header={<Title level={4}>Docketing Data</Title>} size="small">
            <List.Item>
              <AttributeValuePair
                attribute={`Docket Date`}
                value={docketDate}
                valueType="date"
              />
            </List.Item>
            <List.Item>
              <AttributeValuePair
                attribute={`Docket Time`}
                value={docketDate}
                valueType="time"
              />
            </List.Item>
            <List.Item>
              <AttributeValuePair
                attribute={`Effective Date`}
                value={effectiveDate}
                valueType="date"
              />
            </List.Item>
            <List.Item>
              <AttributeValuePair
                attribute={`Effective Time`}
                value={effectiveDate}
                valueType="time"
              />
            </List.Item>
            <List.Item>
              <AttributeValuePair
                attribute={`Clerk/Seq`}
                value={`${clerk}/${clerkSequence}`}
              />
            </List.Item>
          </List>
        </Col>
        <Col span={12}>
          <List header={<Title level={4}>Source Document</Title>} size="small">
            <List.Item>
              <AttributeValuePair attribute={`Type`} value={docType} />
            </List.Item>
            <List.Item>
              <AttributeValuePair attribute={`County`} value={county} />
            </List.Item>
            <List.Item>
              <AttributeValuePair attribute={`Court`} value={court} />
            </List.Item>
            <List.Item>
              <AttributeValuePair
                attribute={totalDebtorsLabel}
                value={totalDebtors}
              />
            </List.Item>
            <List.Item>
              <AttributeValuePair
                attribute={indexNumberLabel}
                value={indexNumber}
              />
              {lienType === '02' && (
                <>
                  &nbsp;
                  <AttributeValuePair
                    attribute={`Date of Accident`}
                    value={accidentDate}
                    valueType="date"
                  />
                </>
              )}
              {(lienType === '03' ||
                lienType === '05' ||
                lienType === '06') && (
                <>
                  &nbsp;
                  <AttributeValuePair
                    attribute={`Doc`}
                    value={documentNumber}
                  />
                </>
              )}
            </List.Item>
          </List>
        </Col>
        {(lienType === '03' ||
          lienType === '04' ||
          lienType === '05' ||
          lienType === '06') && (
          <Col span={12}>
            <List header={<Title level={4}>Premises</Title>} size="small">
              <List.Item>
                <AttributeValuePair
                  attribute={`Block/Lot`}
                  value={`${parseInt(block)}/${parseInt(lot)}`}
                />
              </List.Item>
              {(block === '' || lot === '') && (
                <>
                  <List.Item>
                    <AttributeValuePair
                      attribute={`Address`}
                      value={debtor.address}
                    />
                  </List.Item>
                  <List.Item>
                    <AttributeValuePair
                      attribute={`City`}
                      value={debtor.city}
                    />
                    &nbsp;
                    <AttributeValuePair
                      attribute={`Zip Code`}
                      value={debtor.zipCode}
                    />
                  </List.Item>
                </>
              )}
            </List>
          </Col>
        )}
        {(lienType === '01' || lienType === '03' || lienType === '04') && (
          <Col span={12}>
            <List
              header={<Title level={4}>{attorneyHeader}</Title>}
              size="small"
            >
              <List.Item>
                <AttributeValuePair attribute={`Name`} value={attorney.name} />
                &nbsp;
                <AttributeValuePair attribute={`Type`} value={attorney.type} />
              </List.Item>
              <List.Item>
                <AttributeValuePair
                  attribute={`Address`}
                  value={attorney.address}
                />
              </List.Item>
              <List.Item>
                <AttributeValuePair attribute={`City`} value={attorney.city} />
                &nbsp;
                <AttributeValuePair
                  attribute={`Zip Code`}
                  value={attorney.zipCode}
                />
              </List.Item>
            </List>
          </Col>
        )}
        <Col span={12}>
          <List header={<Title level={4}>{debtorHeader}</Title>} size="small">
            <List.Item>
              <AttributeValuePair attribute={`Name`} value={debtor.name} />
              &nbsp;
              <AttributeValuePair attribute={`Type`} value={debtor.type} />
            </List.Item>
            {(lienType === '01' || lienType === '02' || lienType === '07') && (
              <>
                <List.Item>
                  <AttributeValuePair
                    attribute={`Address`}
                    value={debtor.address}
                  />
                </List.Item>
                <List.Item>
                  <AttributeValuePair attribute={`City`} value={debtor.city} />
                  &nbsp;
                  <AttributeValuePair
                    attribute={`Zip Code`}
                    value={debtor.zipCode}
                  />
                </List.Item>
              </>
            )}
          </List>
        </Col>
        {(lienType === '02' || lienType === '03' || lienType === '04') && (
          <Col span={12}>
            <List header={<Title level={4}>{againsHeader}</Title>} size="small">
              <List.Item>
                <AttributeValuePair attribute={`Name`} value={against.name} />
                &nbsp;
                <AttributeValuePair attribute={`Type`} value={against.type} />
              </List.Item>
              <List.Item>
                <AttributeValuePair
                  attribute={`Address`}
                  value={against.address}
                />
              </List.Item>
              <List.Item>
                <AttributeValuePair attribute={`City`} value={against.city} />
                &nbsp;
                <AttributeValuePair
                  attribute={`Zip Code`}
                  value={against.zipCode}
                />
              </List.Item>
            </List>
          </Col>
        )}
        {(lienType === '01' ||
          lienType === '02' ||
          lienType === '03' ||
          lienType === '04' ||
          lienType === '05' ||
          lienType === '06') && (
          <Col span={12}>
            <List
              header={<Title level={4}>{creditorHeader}</Title>}
              size="small"
            >
              <List.Item>
                <AttributeValuePair attribute={`Name`} value={creditor.name} />
                &nbsp;
                <AttributeValuePair attribute={`Type`} value={creditor.type} />
              </List.Item>
              <List.Item>
                <AttributeValuePair
                  attribute={`Address`}
                  value={creditor.address}
                />
              </List.Item>
              <List.Item>
                <AttributeValuePair attribute={`City`} value={creditor.city} />
                &nbsp;
                <AttributeValuePair
                  attribute={`Zip Code`}
                  value={creditor.zipCode}
                />
              </List.Item>
            </List>
          </Col>
        )}
        <Col span={12}>
          <List
            header={<Title level={4}>{dispositionHeader}</Title>}
            size="small"
          >
            <List.Item>
              <Row type="flex" className="w-100">
                <Col span={12}>
                  <AttributeValuePair
                    attribute={dispositionLabel}
                    value={satisfaction}
                  />
                </Col>
                <Col span={12}>
                  {(lienType === '01' ||
                    lienType === '02' ||
                    lienType === '07') && (
                    <AttributeValuePair
                      attribute={`Sheriff's Execution`}
                      value={sheriffsExecutions}
                    />
                  )}
                </Col>
              </Row>
            </List.Item>
            <List.Item>
              <Row type="flex" className="w-100">
                <Col span={12}>
                  <AttributeValuePair
                    attribute={`Date`}
                    value={
                      satisfaction !== '' && satisfaction !== 'N'
                        ? controlDate
                        : ''
                    }
                    valueType="date"
                  />
                </Col>
                <Col>
                  <AttributeValuePair
                    attribute={`Operator ID`}
                    value={operatorId}
                  />
                </Col>
              </Row>
            </List.Item>
          </List>
        </Col>
        <Col span={24}>
          <Divider dashed />
          <AttributeValuePair
            attribute={`Amount`}
            value={amount}
            valueType="money"
          />
        </Col>
        <Col span={24}>
          {remarks && remarks.length > 0 ? (
            <List
              header={<Title level={4}>Remarks</Title>}
              size="small"
              dataSource={remarks}
              renderItem={(item) => (
                <List.Item>
                  <AttributeValuePair
                    attribute={formatDate(item.remarkDate)}
                    value={item.remarkText}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Text>Remarks: N</Text>
          )}
        </Col>
      </Row>
      {date && (
        <Row type="flex" align="middle" justify="end">
          <Alert
            message={
              <AttributeValuePair
                attribute="Download Date"
                value={date}
                valueType="date"
              />
            }
            type="warning"
          />
        </Row>
      )}
    </Spin>
  );
};

export default LienDetails;
